import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Wrench } from 'lucide-react';
import clsx from 'clsx';
import {
  categories,
  getToolsByCategory,
  tools,
  type CategoryDef,
  type ToolDef,
} from '../registry';

/**
 * `/tools` — entry hub for the seven DevTool categories.
 *
 * Two presentation modes:
 *   1. Default: a 7-card grid of categories with availability counts
 *      (e.g. "1/3 可用"). Clicking a card → `/tools/<id>`.
 *   2. When the user types in the search box: flat list of matching
 *      tools (across categories), preserving the priority badge so
 *      `available > planned` is visually obvious.
 *
 * The search box is the only stateful element on this page; we
 * deliberately don't persist it (R2 — work buffer is not a
 * preference).
 */
export default function ToolboxOverview() {
  const [query, setQuery] = useState('');

  const stats = useMemo(() => {
    return categories.map((c) => {
      const list = getToolsByCategory(c.id);
      return {
        cat: c,
        total: list.length,
        available: list.filter((t) => t.status === 'available').length,
      };
    });
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as ToolDef[];
    return tools.filter((t) => {
      return (
        t.id.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    });
  }, [query]);

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-accent" />
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              开发工具广场 · DevTools
            </h1>
            <span className="badge">{tools.length} 项</span>
          </div>
          <p className="mt-1 text-[12px] text-ink-muted">
            与 Android 无关的通用开发工具：格式化 / 编解码 / 加密 / 生成 / 校验 / 转换 / 网络。
            全部在浏览器内运行，不会上传任何数据。
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-dim" />
          <input
            className="input pl-7"
            style={{ width: 240 }}
            placeholder="搜索工具…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </header>

      {query.trim().length === 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {stats.map(({ cat, total, available }) => (
            <CategoryCard key={cat.id} cat={cat} total={total} available={available} />
          ))}
        </div>
      ) : matches.length === 0 ? (
        <div className="card-pad text-center text-sm text-ink-muted">
          没有匹配「<span className="font-mono text-ink">{query}</span>」的工具。
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="text-[11px] uppercase tracking-wide text-ink-dim">
            搜索结果 · {matches.length} 项
          </div>
          {matches.map((t) => (
            <SearchResultRow key={t.path} tool={t} />
          ))}
        </div>
      )}

    </div>
  );
}

function CategoryCard({
  cat,
  total,
  available,
}: {
  cat: CategoryDef;
  total: number;
  available: number;
}): JSX.Element {
  const Icon = cat.icon;
  return (
    <Link to={`/tools/${cat.id}`} className="block">
      <div className="card-pad group h-full transition hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-accent" />
          <span className="font-medium text-ink">{cat.label}</span>
          <ArrowRight className="ml-auto h-4 w-4 text-ink-dim transition group-hover:translate-x-0.5 group-hover:text-accent" />
        </div>
        <p className="mt-1 text-[12px] leading-snug text-ink-muted">{cat.description}</p>
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-ink-dim">
          <span className="rounded bg-bg-soft px-1.5 py-0.5 font-mono">
            {available}/{total} 可用
          </span>
          <span className="font-mono">/tools/{cat.id}</span>
        </div>
      </div>
    </Link>
  );
}

function SearchResultRow({ tool }: { tool: ToolDef }): JSX.Element {
  const Icon = tool.icon;
  const isAvailable = tool.status === 'available';
  return (
    <Link
      to={tool.path}
      className={clsx(
        'group flex items-center gap-2 rounded-md border border-line bg-bg-card px-3 py-2 transition',
        isAvailable ? 'hover:border-accent/40 hover:bg-accent-soft/30' : 'opacity-70',
      )}
    >
      <Icon className={clsx('h-4 w-4', isAvailable ? 'text-accent' : 'text-ink-muted')} />
      <span className="font-medium text-ink">{tool.name}</span>
      <span className="text-[11px] text-ink-dim">— {tool.description}</span>
      <span className="ml-auto rounded bg-bg-soft px-1.5 py-0.5 font-mono text-[10px] text-ink-muted">
        {tool.category}
      </span>
      {!isAvailable && (
        <span className="rounded border border-amber-500/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-500 dark:text-amber-400">
          规划中
        </span>
      )}
    </Link>
  );
}
